/**
 * The photo-documentation engine's pure half (`BACKLOG.md` M9): angle/lighting standardization
 * guidance as data, and the timeline-grouping function the UI renders rather than re-deriving.
 *
 * **No binary image data lives in this schema, ever.** `server/src/db/schema/aesthetics.ts`'s
 * `aestheticsPatientPhotos.storageRef` is a pointer, matching `legal.ts`'s own `legalDocuments.
 * contentUrl` reasoning exactly: this system's Postgres is not an object store, and treating it as
 * one would make every future export/backup/replication decision also a PHI-blob decision. Where the
 * actual bytes are stored (S3-with-BAA once Phase 5's AWS build lands, per `docs/roadmap.md` §5) is
 * out of scope for this pass — this module only shapes the *metadata* a chart needs to reference and
 * order photos by.
 */

/**
 * Standard clinical photo angles. `frontal` plus the four oblique/lateral pairs is the smallest set
 * that actually supports before/after comparison for a facial aesthetic procedure — the same five
 * angles AmSpa's and Aesthetic Record's own documentation (`BACKLOG.md` M9's cited sources) treat as
 * baseline. `other` exists for a body-area or non-facial photo this fixed set does not name.
 */
export const PHOTO_ANGLES = [
  'frontal',
  'left_oblique',
  'right_oblique',
  'left_lateral',
  'right_lateral',
  'other',
] as const;
export type PhotoAngle = (typeof PHOTO_ANGLES)[number];

const PHOTO_ANGLE_LABELS: Record<PhotoAngle, string> = {
  frontal: 'Frontal',
  left_oblique: 'Left oblique (45°)',
  right_oblique: 'Right oblique (45°)',
  left_lateral: 'Left lateral (90°/profile)',
  right_lateral: 'Right lateral (90°/profile)',
  other: 'Other',
};

function includes(list: readonly string[], value: string): boolean {
  return list.includes(value);
}

export function isPhotoAngle(value: string): value is PhotoAngle {
  return includes(PHOTO_ANGLES, value);
}

export function photoAngleLabel(angle: string): string {
  return isPhotoAngle(angle) ? PHOTO_ANGLE_LABELS[angle] : angle;
}

/**
 * Fixed guidance text for standardizing angle and lighting across visits — the actual point of
 * before/after comparability. This is display copy, not an enforced rule: `lightingStandardized`
 * (`aestheticsPatientPhotos`) is a self-reported flag the person taking the photo sets, because
 * nothing server-side can verify real-world lighting conditions from a stored pointer alone. Kept as
 * data here (not hand-typed into the UI component) so a future revision to the guidance is a
 * one-line change reused by every renderer, matching `shared/src/help/topics.ts`'s own "one shared
 * source of truth" reasoning for in-app help copy.
 */
export const PHOTO_STANDARDIZATION_GUIDANCE: readonly string[] = [
  'Use the same camera distance and height every visit — stand at arm’s length or mount the camera at a fixed station.',
  'Neutral, even lighting with no strong shadows — diffuse overhead or ring light, not a single side lamp or direct sun.',
  'Plain, consistent background across visits, with the patient in the same position (seated or standing) each time.',
  'Relaxed, neutral facial expression unless the angle specifically calls for an animated one (e.g. a forehead-raise for a neuromodulator baseline).',
  'Capture all five standard angles at intake and before/after any treatment that could change facial contour, even if only one area is being treated.',
];

/** One photo record's minimum shape for grouping into a timeline — a structural subset of
 * `PatientPhotoResponse` (`aestheticsRequests.ts`) so this function takes no dependency on that
 * schema module and stays trivially unit-testable with plain object literals. */
export interface TimelinePhoto {
  readonly id: string;
  readonly takenAt: string;
  readonly angle: string;
}

/** One visit's worth of photos, grouped by calendar date (the `YYYY-MM-DD` prefix of `takenAt`,
 * matching `columns.ts`'s own instant-vs-calendar-date split: a timeline groups by the day a photo
 * was taken, not by its exact instant) and ordered newest-first — a before/after review works
 * backward from today, not forward from intake. */
export interface PhotoTimelineEntry {
  readonly date: string;
  readonly photos: readonly TimelinePhoto[];
}

/**
 * Group a patient's photos into a same-day timeline, newest date first, each day's own photos
 * ordered by angle in `PHOTO_ANGLES`'s own fixed order so a reviewer sees frontal, then the two
 * obliques, then the two laterals, every time — not whatever order they happened to be uploaded in.
 * Pure so the web app never re-derives this grouping ad hoc per render, and so a change to the
 * grouping rule (e.g. "group by encounter instead of calendar day") is one function to update.
 */
export function groupPhotosIntoTimeline(
  photos: readonly TimelinePhoto[],
): readonly PhotoTimelineEntry[] {
  const angleOrder = new Map<string, number>(PHOTO_ANGLES.map((angle, index) => [angle, index]));
  const byDate = new Map<string, TimelinePhoto[]>();

  for (const photo of photos) {
    const date = photo.takenAt.slice(0, 10);
    const existing = byDate.get(date);
    if (existing === undefined) byDate.set(date, [photo]);
    else existing.push(photo);
  }

  const entries = [...byDate.entries()].map(([date, entryPhotos]) => ({
    date,
    photos: [...entryPhotos].sort((a, b) => {
      const orderA = angleOrder.get(a.angle) ?? PHOTO_ANGLES.length;
      const orderB = angleOrder.get(b.angle) ?? PHOTO_ANGLES.length;
      return orderA - orderB;
    }),
  }));

  return entries.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
}
